var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
import * as React from 'react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
/**
 * React host for an FXP shared component published by another partner app.
 *
 * The FXP shell registers its loader as a **W3C custom element** using Angular Elements
 * (`ng2Main.bundle.js`, `AppComponent` constructor):
 *
 *   const el = createCustomElement(FxpSharedComponentLoader, { injector });
 *   customElements.define('fxp-shared-component-control', el);
 *
 * Because it is a custom element rather than an Angular directive, a React app can host it by
 * rendering the tag — the shell already owns the Angular runtime, so no `@angular/*` or `zone.js`
 * dependency is added here. `@fxp/fxpsharedcomponentloader` exports both an
 * `AngularComponentRenderingManager` and a `ReactComponentRenderingManager` (its template holds
 * `#angularContainer` + `#reactContainer`), so it renders producers of either framework and lazy
 * loads the producing app's bundle itself.
 *
 * Loader inputs, from `FxpSharedComponentLoader.propDecorators`:
 * `appname`, `componentname`, `componentinputs`, `getcallbacks`, `feedbackenabled`,
 * `feedbacksurveyid`; output `oncallback`.
 */
export const FXP_SHARED_COMPONENT_TAG = 'fxp-shared-component-control';
const getRegistry = (injected) => {
    if (injected) {
        return injected;
    }
    return typeof window !== 'undefined' ? window.customElements : undefined;
};
/**
 * Tracks whether the loader element is registered. The shell defines it during its own bootstrap,
 * which can land after a React route has already mounted, so a one-shot check at first render is
 * not enough — we also wait on `whenDefined`.
 */
export const useIsFxpLoaderDefined = (registry) => {
    const resolved = getRegistry(registry);
    const [defined, setDefined] = useState(() => !!(resolved === null || resolved === void 0 ? void 0 : resolved.get(FXP_SHARED_COMPONENT_TAG)));
    useEffect(() => {
        if (!resolved || defined) {
            return undefined;
        }
        let cancelled = false;
        Promise.resolve(resolved.whenDefined(FXP_SHARED_COMPONENT_TAG))
            .then(() => {
            if (!cancelled) {
                setDefined(true);
            }
        })
            .catch(() => {
            // Registry unavailable or rejected; the fallback stays.
        });
        return () => {
            cancelled = true;
        };
    }, [resolved, defined]);
    return defined;
};
export const FxpSharedComponent = ({ appName, componentName, inputs, getCallbacks, feedbackEnabled, feedbackSurveyId, onCallback, className, style, fallback = null, forceFallback = false, fallbackDelayMs = 1200, renderTimeoutMs = 30000, registry, }) => {
    const containerRef = useRef(null);
    const hostRef = useRef(null);
    const isDefined = useIsFxpLoaderDefined(registry);
    const [painted, setPainted] = useState(false);
    const [placeholderDue, setPlaceholderDue] = useState(fallbackDelayMs <= 0);
    const shouldRender = isDefined && !forceFallback;
    // Latest inputs, read during element creation without making creation depend on identity.
    const latest = useRef({ inputs, getCallbacks, feedbackEnabled, feedbackSurveyId });
    latest.current = { inputs, getCallbacks, feedbackEnabled, feedbackSurveyId };
    useEffect(() => {
        setPainted(false);
        setPlaceholderDue(fallbackDelayMs <= 0);
    }, [appName, componentName, fallbackDelayMs]);
    // Hold the placeholder back briefly so a fast shared-component paint never flashes it.
    useEffect(() => {
        if (!shouldRender || placeholderDue || painted) {
            return undefined;
        }
        const timer = window.setTimeout(() => setPlaceholderDue(true), fallbackDelayMs);
        return () => window.clearTimeout(timer);
    }, [shouldRender, placeholderDue, painted, fallbackDelayMs]);
    /*
     * The element is created imperatively and populated BEFORE it is connected to the document.
     * `FxpSharedComponentLoader` reads `appname`/`componentname` during its initialization, which
     * Angular Elements runs from `connectedCallback`. Rendering the tag through JSX and assigning in
     * an effect is too late — the loader initializes with `appname === undefined` and logs
     * "Mandatory parameter <appName> is missing". Angular Elements buffers property writes made
     * before connection, so setting them first is both safe and required.
     */
    useLayoutEffect(() => {
        var _a;
        const container = containerRef.current;
        if (!container || !shouldRender) {
            return undefined;
        }
        const host = document.createElement(FXP_SHARED_COMPONENT_TAG);
        host.appname = appName;
        host.componentname = componentName;
        host.componentinputs = (_a = latest.current.inputs) !== null && _a !== void 0 ? _a : {};
        if (latest.current.getCallbacks !== undefined) {
            host.getcallbacks = latest.current.getCallbacks;
        }
        if (latest.current.feedbackEnabled !== undefined) {
            host.feedbackenabled = latest.current.feedbackEnabled;
        }
        if (latest.current.feedbackSurveyId !== undefined) {
            host.feedbacksurveyid = latest.current.feedbackSurveyId;
        }
        host.setAttribute('data-testid', 'fxp-shared-component');
        host.setAttribute('data-appname', appName);
        host.setAttribute('data-componentname', componentName);
        container.appendChild(host);
        hostRef.current = host;
        return () => {
            hostRef.current = null;
            host.remove();
        };
    }, [shouldRender, appName, componentName]);
    // Later input changes go straight to the live element (parity of legacy's `[component-inputs]`).
    useEffect(() => {
        if (hostRef.current) {
            hostRef.current.componentinputs = inputs !== null && inputs !== void 0 ? inputs : {};
        }
    }, [inputs]);
    useEffect(() => {
        const host = hostRef.current;
        if (!host || !onCallback) {
            return undefined;
        }
        const handler = (event) => onCallback(event.detail);
        host.addEventListener('oncallback', handler);
        return () => host.removeEventListener('oncallback', handler);
    }, [shouldRender, onCallback]);
    /*
     * First-paint detection. The loader reports failure only by logging and rejecting internally, and
     * lazy-loading the producing app's bundles can take seconds on a cold cache, so we watch the host
     * for layout instead of guessing a deadline. Until it paints, `fallback` stands in; the shared
     * component itself is never unmounted, so a late paint still takes over.
     */
    useEffect(() => {
        if (!shouldRender || painted) {
            return undefined;
        }
        const host = hostRef.current;
        if (!host) {
            return undefined;
        }
        let cancelled = false;
        const hasPainted = () => {
            if (cancelled) {
                return false;
            }
            const rect = host.getBoundingClientRect();
            if (rect.height > 0 || rect.width > 0) {
                setPainted(true);
                return true;
            }
            return false;
        };
        if (hasPainted()) {
            return undefined;
        }
        let observer;
        // ResizeObserver isn't in this project's TS DOM lib; access it defensively (jsdom lacks it too).
        const RO = window.ResizeObserver;
        if (RO) {
            const ro = new RO(() => {
                if (hasPainted()) {
                    ro.disconnect();
                }
            });
            ro.observe(host);
            observer = ro;
        }
        const startedAt = Date.now();
        const poll = window.setInterval(() => {
            const done = hasPainted();
            const expired = renderTimeoutMs > 0 && Date.now() - startedAt >= renderTimeoutMs;
            if (done || expired) {
                window.clearInterval(poll);
            }
        }, 400);
        return () => {
            cancelled = true;
            observer === null || observer === void 0 ? void 0 : observer.disconnect();
            window.clearInterval(poll);
        };
    }, [shouldRender, painted, renderTimeoutMs, appName, componentName]);
    if (!shouldRender) {
        return React.createElement(React.Fragment, null, fallback);
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: containerRef, className: className, style: style }),
        !painted && placeholderDue ? React.createElement(React.Fragment, null, fallback) : null));
};
export default FxpSharedComponent;
//# sourceMappingURL=FxpSharedComponent.js.map